import { useState, useEffect } from 'react';
import { API_BASE_URL, errorMessageFromBody, isNetworkError, NETWORK_ERROR_MESSAGE } from '../../../services/api';
import { DataFreshnessBanner, PREPARING_POLL, PreparingNotice, useBackgroundRefresh } from './DataFreshness';

// ─── TypeScript interfaces for API response ───────────────────────────────────

interface BrandVoice {
  // null when the user selected none — shown as "Not set".
  primary_tone: string | null;
  personality: string | null;
  communication_signature: string | null;
  content_guidelines: string[];
}

interface PlatformDirection {
  name: string;
  icon: string;
  tone_direction: string;
  communication_style: string;
  platform_role: string;
  objective_mapping: string;
  // null when there aren't enough posts yet to derive them from history.
  post_frequency: string | null;
  best_posting_times: string | null;
  // tone_direction / communication_style are generic (AI call failed).
  is_generic?: boolean;
}

interface NarrativePhase {
  phase: string;
  behavior: string;
  content_types: string[];
}

interface CommunicationDirectionData {
  brand_voice: BrandVoice;
  platforms: PlatformDirection[];
  narrative_behavior: NarrativePhase[];
  ai_content_generation_rules: string[];
  // Sections holding generic guidance because the AI call failed this time
  // (e.g. "content_guidelines"). Absent in older cached responses.
  generic_sections?: string[];
  // When it was made; whether a new one is being generated in the background
  // (brand voice or platforms changed); whether the first one is being prepared.
  updated_at?: string | null;
  refreshing?: boolean;
  preparing?: boolean;
}

// Marks a section showing generic guidance instead of the brand's own.
const GenericBadge = () => (
  <span
    className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-semibold text-amber-700 uppercase tracking-wide align-middle"
    title="We couldn't personalise this right now — showing generic guidance."
  >
    Generic
  </span>
);

export default function CommunicationDirection() {
  const [activePlatform, setActivePlatform] = useState(0);
  const [directionData, setDirectionData] = useState<CommunicationDirectionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      setFetchError('User not authenticated');
      setIsLoading(false);
      return;
    }
    const token = localStorage.getItem('access_token');
    setIsLoading(true);
    setFetchError(null);
    fetch(`${API_BASE_URL}/smm/strategy/communication-direction/${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errorMessageFromBody(errBody, res.status));
        }
        return res.json();
      })
      .then((data: CommunicationDirectionData) => {
        setDirectionData(data);
        setActivePlatform(0);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load communication direction data:', err);
        setFetchError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || 'Please try again.');
        setIsLoading(false);
      });
  }, [reloadKey]);

  // While a new direction is generated (or the first one prepared), re-ask
  // quietly and swap it in when ready. No Refresh button: it follows the
  // brand-voice settings and connected platforms.
  const refetchQuietly = async () => {
    const userId = localStorage.getItem('user_id');
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${API_BASE_URL}/smm/strategy/communication-direction/${userId}`, {
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (res.ok) setDirectionData(await res.json());
  };
  const freshness = useBackgroundRefresh(directionData?.refreshing, refetchQuietly);
  const preparing = useBackgroundRefresh(directionData?.preparing, refetchQuietly, PREPARING_POLL);

  if (isLoading) {
    return (
      <div className="space-y-6 w-full animate-pulse">
        {/* Brand Voice skeleton */}
        <div className="bg-[#fdfffc] rounded-xl border border-gray-200/80 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-gray-200 rounded-xl" />
            <div>
              <div className="h-4 bg-gray-200 rounded w-36 mb-1.5" />
              <div className="h-3 bg-gray-100 rounded w-52" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="h-2.5 bg-gray-300 rounded w-20 mb-3" />
                <div className="h-5 bg-gray-200 rounded w-32" />
              </div>
            ))}
          </div>
          <div className="h-2.5 bg-gray-300 rounded w-32 mb-3" />
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-start gap-3 p-3.5 bg-gray-50 border border-gray-200/60 rounded-lg">
                <div className="w-6 h-6 bg-gray-200 rounded-full flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded w-full mb-1.5" />
                  <div className="h-3 bg-gray-100 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Platform tabs skeleton */}
        <div className="bg-[#fdfffc] rounded-xl border border-gray-200/80 p-6 shadow-sm">
          <div className="h-4 bg-gray-200 rounded w-56 mb-5" />
          <div className="flex gap-2 mb-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-9 bg-gray-200 rounded-lg w-24" />
            ))}
          </div>
          <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded-lg" />
              <div className="h-5 bg-gray-200 rounded w-24" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i}>
                  <div className="h-2.5 bg-gray-300 rounded w-24 mb-1.5" />
                  <div className="h-3.5 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Narrative map skeleton */}
        <div className="bg-[#fdfffc] rounded-xl border border-gray-200/80 p-6 shadow-sm">
          <div className="h-4 bg-gray-200 rounded w-44 mb-5" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                  <div className="h-3.5 bg-gray-200 rounded w-16" />
                </div>
                <div className="h-2.5 bg-gray-300 rounded w-20 mb-1.5" />
                <div className="h-3 bg-gray-200 rounded w-full mb-3" />
                <div className="flex gap-1.5 flex-wrap">
                  {[...Array(2)].map((_, j) => (
                    <div key={j} className="h-6 bg-gray-200 rounded w-16" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (fetchError || !directionData) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
        <p className="font-semibold">Failed to load communication direction data.</p>
        {fetchError && <p className="mt-1">{fetchError}</p>}
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-3 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-xs font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (directionData.preparing) {
    return <PreparingNotice state={preparing} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  const genericSections = new Set(directionData.generic_sections || []);
  const guidelinesGeneric = genericSections.has('content_guidelines');
  const narrativeGeneric = genericSections.has('narrative_behavior');
  const rulesGeneric = genericSections.has('ai_content_generation_rules');
  const anyGeneric =
    guidelinesGeneric || narrativeGeneric || rulesGeneric || directionData.platforms.some((p) => p.is_generic);

  return (
    <div className="space-y-6 w-full bg-[#fdfffc] text-[#222222] p-6 rounded-xl border border-gray-100 shadow-sm">
      <DataFreshnessBanner updatedAt={directionData.updated_at} state={freshness} />
      {anyGeneric && (
        <div className="flex items-start justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <div>
            <p className="font-semibold">Generic guidance — we couldn't personalise this right now.</p>
            <p className="mt-1">Sections marked "Generic" aren't tailored to your brand yet. Retry, or check back later.</p>
          </div>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="flex-shrink-0 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg text-xs font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Brand Voice Overview */}
      <div className="bg-[#fdfffc] rounded-xl border border-gray-200/80 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0859b8]/10">
            <i className="ri-mic-line text-[#0859b8] text-lg"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#222222]">Brand Voice & Personality</h3>
            <p className="text-sm text-gray-500">How your brand communicates across all touchpoints</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-[#fdfffc] rounded-lg border border-gray-200">
            <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-2">Primary Tone Selected</p>
            {directionData.brand_voice.primary_tone ? (
              <p className="text-lg font-semibold text-[#222222]">{directionData.brand_voice.primary_tone}</p>
            ) : (
              <p className="text-lg font-semibold text-gray-400">Not set</p>
            )}
          </div>
          <div className="p-4 bg-[#fdfffc] rounded-lg border border-gray-200">
            <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-2">Personality Selected</p>
            {directionData.brand_voice.personality ? (
              <p className="text-lg font-semibold text-[#222222]">{directionData.brand_voice.personality}</p>
            ) : (
              <p className="text-lg font-semibold text-gray-400">Not set</p>
            )}
          </div>
        </div>

        <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-3">
          Content Guidelines Based on Brand Voice & Personality
          {guidelinesGeneric && <GenericBadge />}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(directionData.brand_voice.content_guidelines || []).map((g, i) => (
            <div key={i} className="flex items-start gap-3 p-3.5 bg-[#fdfffc] border border-gray-200/60 rounded-lg hover:border-gray-300 transition-colors">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0859b8]/10 flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-[#0859b8]">{i + 1}</span>
              </div>
              <p className="text-sm text-[#222222]">{g}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Platform-Wise Communication Direction */}
      <div className="bg-[#fdfffc] rounded-xl border border-gray-200/80 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-[#222222] mb-5">Platform Communication Direction</h3>

        {directionData.platforms.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            No platforms connected yet. Connect your social accounts to see platform direction.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-5">
              {directionData.platforms.map((p, i) => (
                <button
                  key={p.name}
                  onClick={() => setActivePlatform(i)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${activePlatform === i
                    ? 'bg-[#0859b8] text-white hover:bg-[#0859b8]/90 shadow-sm'
                    : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80'
                    }`}
                >
                  <i className={`${p.icon} text-base`}></i>
                  {p.name}
                </button>
              ))}
            </div>

            <div className="p-5 bg-[#fdfffc] rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <i className={`${directionData.platforms[activePlatform].icon} text-2xl text-[#0859b8]`}></i>
                <h4 className="text-lg font-bold text-[#222222]">
                  {directionData.platforms[activePlatform].name}
                  {directionData.platforms[activePlatform].is_generic && <GenericBadge />}
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-1">Tone Direction</p>
                  <p className="text-sm text-[#222222] font-medium">{directionData.platforms[activePlatform].tone_direction}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-1">Content Style</p>
                  <p className="text-sm text-[#222222]">{directionData.platforms[activePlatform].communication_style}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-1">Post Frequency</p>
                  {directionData.platforms[activePlatform].post_frequency ? (
                    <p className="text-sm font-semibold text-[#222222]">{directionData.platforms[activePlatform].post_frequency}</p>
                  ) : (
                    <p className="text-sm text-gray-500">Not enough posts yet to estimate</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-1">Best Posting Times</p>
                  {directionData.platforms[activePlatform].best_posting_times ? (
                    <p className="text-sm font-semibold text-[#222222]">{directionData.platforms[activePlatform].best_posting_times}</p>
                  ) : (
                    <p className="text-sm text-gray-500">Not enough posts yet to suggest times</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Narrative Behavior Map */}
      <div className="bg-[#fdfffc] rounded-xl border border-gray-200/80 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-[#222222] mb-5">
          Narrative Behavior Map
          {narrativeGeneric && <GenericBadge />}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(directionData.narrative_behavior || []).map((phase, i) => (
            <div key={phase.phase} className="p-4 bg-[#fdfffc] rounded-xl border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0859b8]/10 text-[#0859b8] text-sm font-bold">
                  {i + 1}
                </div>
                <h4 className="font-bold text-[#222222] text-sm">{phase.phase}</h4>
              </div>
              <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-1">Behavior</p>
              <p className="text-sm text-[#222222] font-medium mb-3">{phase.behavior}</p>
              <p className="text-xs font-semibold text-[#0859b8] uppercase tracking-wide mb-1">Content Types</p>
              <div className="flex flex-wrap gap-1.5">
                {(phase.content_types || []).map((ct) => (
                  <span key={ct} className="px-2 py-1 bg-[#fdfffc] border border-gray-200 rounded text-xs text-[#222222] hover:bg-gray-50 transition-colors">
                    {ct}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Content Generation Rules */}
      <div className="bg-[#fdfffc] rounded-xl border border-gray-200/80 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0859b8]/10">
            <i className="ri-robot-2-line text-[#0859b8] text-lg"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#222222]">
              Content Generation Rules
              {rulesGeneric && <GenericBadge />}
            </h3>
            <p className="text-sm text-gray-500">Guidelines that we follow when generating content for your brand</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(directionData.ai_content_generation_rules || []).map((rule, i) => (
            <div key={i} className="flex items-start gap-3 p-3.5 bg-[#fdfffc] border border-[#0859b8]/15 hover:border-[#0859b8]/35 rounded-lg transition-colors">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00adc4]/10 flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-xs text-[#00adc4] font-bold"></i>
              </div>
              <p className="text-sm text-[#222222]">{rule}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
